import type { PlanAction, PlanScheduleKind, PlanStatus } from '@/client';

/**
 * The one part of the plan's vocabulary the generated client cannot express yet.
 *
 * Everything else — `status`, `category`, `source_origin`, `node_type`, `allowed_actions` — arrives
 * from `@/client` as a real union, so it is imported where it is used and not redeclared here. A
 * second copy would only be a second thing to keep in step.
 *
 * `kind` is the exception: `maintenance/v2/schemas.py` still widens it to `str` on the way out, so
 * it reaches us as `string` and the closed set has to live somewhere. When that `Literal` is
 * propagated, this file loses `PlanKind` and `asPlanKind` too, and what remains is the two tables
 * below — which are frontend decisions, not a copy of a backend type.
 */

/**
 * `plan_nodes.py` — `_LEAF_KIND` values, the four `_GROUP_PREFIX` kinds, and `_DISPLAY_ONLY`.
 *
 * The group kinds were missing until a staging response arrived carrying `duty_group`, which the
 * guard below quietly turned into `null`. It happened to render correctly — a duty group is neither
 * display-only nor a warranty, so both predicates wanted `false` anyway — which is precisely why it
 * went unnoticed. The set is taken from the backend's own tables now rather than from the values a
 * single property happened to return.
 */
export const PlanKind = {
	// _LEAF_KIND
	ExplicitTask: 'explicit_task',
	Checklist: 'checklist',
	Compliance: 'compliance',
	WarrantyExpiry: 'warranty_expiry',
	Custom: 'custom',
	// _GROUP_PREFIX
	WarrantySystem: 'warranty_system',
	SsGroup: 'ss_group',
	DutyGroup: 'duty_group',
	Awareness: 'awareness',
	// _DISPLAY_ONLY
	CoverageClause: 'coverage_clause',
	ProcedureStep: 'procedure_step',
} as const;
export type PlanKind = (typeof PlanKind)[keyof typeof PlanKind];

const KINDS = new Set<string>(Object.values(PlanKind));

/**
 * Fail closed on an unrecognised kind.
 *
 * Worth keeping where the other narrowings were not: those guarded fields the client types as a
 * union, where an unknown value only ever loses an equality check. `kind` is genuinely `string`, and
 * it decides whether a node is display-only and whether it draws as a warranty — so a value this
 * build has never seen has to read as "none of them" rather than fall through a comparison.
 */
export const asPlanKind = (value: string | null | undefined): PlanKind | null =>
	value && KINDS.has(value) ? (value as PlanKind) : null;

/**
 * Why a household turned a card down, sent as `reason` in the dismiss action's params.
 *
 * Hand-written, unlike the verbs beside it, because it cannot be generated: the actions endpoint
 * takes a raw `Request` so it can content-negotiate JSON against multipart, which leaves it with no
 * declared body in the OpenAPI spec — `body?: never` in the generated client. `PlanAction` only
 * reaches us because a *response* field references it. Anything named solely by a request, this
 * included, is invisible to the generator however often it is re-run.
 *
 * Closed on purpose. The endpoint validates against its own `DismissReason` and answers 422 on
 * anything off-vocabulary, so a value we invent fails loudly rather than being recorded as noise.
 *
 * "I already did this" is deliberately absent. That is a completion, not a rejection — dismissing a
 * valid recurring task would hide every future occurrence of it too.
 */
export const DismissReasons = {
	/** Already on the plan somewhere else — points at entity resolution. */
	Duplicate: 'duplicate',
	/** Does not apply to this property. */
	NotApplicable: 'not_applicable',
	/** Wrong or nonsense — points at extraction. */
	Incorrect: 'incorrect',
	/** Something else; pairs with a free-text note. */
	Other: 'other',
} as const;

export type DismissReason = (typeof DismissReasons)[keyof typeof DismissReasons];

/** What the endpoint trims a `reason_note` to. Stated here so the field can stop the typing first. */
export const DISMISS_NOTE_MAX_LENGTH = 500;

/** Kinds the plan renders but nobody can act on — `allowed_actions` is empty for these. */
export const DISPLAY_ONLY_KINDS: ReadonlySet<PlanKind> = new Set([PlanKind.CoverageClause, PlanKind.ProcedureStep]);

/**
 * The statuses, named — so no call site spells one by hand.
 *
 * `satisfies` checks every value against the generated union, so a typo is a compile error with a
 * suggestion, and the guard below fails the build if the union gains a status this does not name.
 */
export const PlanStatuses = {
	Upcoming: 'upcoming',
	DueSoon: 'due_soon',
	Overdue: 'overdue',
	Expired: 'expired',
	Completed: 'completed',
	Snoozed: 'snoozed',
	Skipped: 'skipped',
	Dismissed: 'dismissed',
	WorthDoing: 'worth_doing',
} as const satisfies Record<string, PlanStatus>;

type UnnamedPlanStatus = Exclude<PlanStatus, (typeof PlanStatuses)[keyof typeof PlanStatuses]>;
const _planStatusesAreComplete: [UnnamedPlanStatus] extends [never] ? true : ['unnamed status:', UnnamedPlanStatus] =
	true;
void _planStatusesAreComplete;

/**
 * A passed general one-off, which the generated `PlanStatus` union does not carry yet.
 *
 * `maintenance/v2` returns it for a suggestion whose date has gone by without the household
 * adopting it: still worth doing, explicitly not overdue, and it does not roll forward the way a
 * general recurrence does. Named here for the reason `PlanKind` is — the value is real and the
 * client cannot spell it — so the assertion lives at this one site instead of at every comparison
 * and every fixture. When the union gains it, `_planStatusesAreComplete` above fails the build and
 * this constant goes with the fix.
 */
export const WORTH_DOING = PlanStatuses.WorthDoing;

/** True for a suggestion whose date has passed. See `WORTH_DOING`. */
export const isWorthDoing = (status: string | null | undefined): boolean => status === WORTH_DOING;

/**
 * Statuses that mean the work is behind — what the schedule groups under Overdue.
 *
 * From the plan rather than from the calendar: `status` accounts for what a date comparison cannot
 * see — a snooze, a skip, a completion recorded today — so a node whose date has passed but which
 * the plan calls `snoozed` is not behind.
 */
export const BEHIND_STATUSES: ReadonlySet<PlanStatus> = new Set([PlanStatuses.Overdue, PlanStatuses.Expired]);

/**
 * How an obligation is scheduled, named — because the generated type is widened.
 *
 * `PlanScheduleOut.kind` arrives as `PlanScheduleKind | string`, so a hand-spelled comparison is
 * not checked: `as_needed` mistyped compiles and silently sorts every card into the other tab.
 * `satisfies` pins these to the union, and the guard below fails the build if it gains a member.
 */
export const PlanScheduleKinds = {
	Recurring: 'recurring',
	OneOff: 'one_off',
	AsNeeded: 'as_needed',
} as const satisfies Record<string, PlanScheduleKind>;

type UnnamedPlanScheduleKind = Exclude<PlanScheduleKind, (typeof PlanScheduleKinds)[keyof typeof PlanScheduleKinds]>;
const _planScheduleKindsAreComplete: [UnnamedPlanScheduleKind] extends [never]
	? true
	: ['unnamed schedule kind:', UnnamedPlanScheduleKind] = true;
void _planScheduleKindsAreComplete;

/**
 * Which surface a kept card sits on.
 *
 * Unlike the other vocabularies here there is no generated union to pin against — the endpoint types
 * `ReviewOut.placement` as a plain nullable string — so these two values are the whole contract and
 * a third one arriving would read as neither.
 *
 * Resolved by the server at keep time and stored, never re-derived: `category` is recomputed on
 * every run, so deriving the surface from it would move cards between tabs on a reprocess. It is
 * null until a card is kept, because placement is a property of keeping.
 */
export const PlanPlacements = {
	Schedule: 'schedule',
	General: 'general',
} as const;

export type PlanPlacement = (typeof PlanPlacements)[keyof typeof PlanPlacements];

/**
 * Statuses that say the occasion the card is showing has already been dealt with.
 *
 * Not the same as "in the past". `overdue` and `expired` are behind and still want an answer, and a
 * `snoozed` occurrence has only moved — these two are where the question has been answered. Read
 * `PlanNodeOut.status` with it, which is the *current* occurrence's state, so a recurring card that
 * has rolled forward reads `upcoming` again and is correctly not settled.
 */
export const SETTLED_STATUSES: ReadonlySet<PlanStatus> = new Set([PlanStatuses.Completed, PlanStatuses.Skipped]);

/**
 * The statuses that are still work ahead, in the order a filter should offer them.
 *
 * Not `SETTLED_STATUSES`, which is the narrower "this occasion was dealt with" pair and leaves out
 * `expired` and `snoozed`. History reads the rest unconditionally — see `planHistoryQuery`.
 */
export const SCHEDULED_STATUSES: readonly PlanStatus[] = [
	PlanStatuses.Overdue,
	PlanStatuses.DueSoon,
	PlanStatuses.Upcoming,
];

const SCHEDULED_STATUS_SET: ReadonlySet<string> = new Set(SCHEDULED_STATUSES);

/** A URL or a restored preference can still name a status no control offers — see `SCHEDULED_STATUSES`. */
export const isScheduledStatus = (value: string): value is PlanStatus => SCHEDULED_STATUS_SET.has(value);

/** Behind, or close enough to want attention — what a badge or a sort would use. */
export const ATTENTION_STATUSES: ReadonlySet<PlanStatus> = new Set([
	PlanStatuses.Overdue,
	PlanStatuses.Expired,
	PlanStatuses.DueSoon,
]);

/**
 * The actions, named — so no call site spells one by hand.
 *
 * Same contract as `PlanStatuses`: `satisfies` checks every value against the generated union, and
 * the guard below fails the build if the API gains an action this does not name. That matters more
 * here than for a status, because an unnamed action is one the drawer silently cannot offer.
 */
export const PlanActions = {
	Complete: 'complete',
	Uncomplete: 'uncomplete',
	Snooze: 'snooze',
	Skip: 'skip',
	Note: 'note',
	Attach: 'attach',
	Accept: 'accept',
	Unaccept: 'unaccept',
	// `keep` replaced `accept` + `acknowledge`: one verb meaning "this card belongs in my plan",
	// which the backend guarantees survives a reconcile. `accept` remains for doc-derived cards.
	Keep: 'keep',
	Unkeep: 'unkeep',
	Dismiss: 'dismiss',
	Restore: 'restore',
	Reschedule: 'reschedule',
	ClearReschedule: 'clear_reschedule',
	Edit: 'edit',
	Delete: 'delete',
	Collapse: 'collapse',
	Rename: 'rename',
	BulkComplete: 'bulk_complete',
	BulkDismiss: 'bulk_dismiss',
} as const satisfies Record<string, PlanAction>;

type UnnamedPlanAction = Exclude<PlanAction, (typeof PlanActions)[keyof typeof PlanActions]>;
const _planActionsAreComplete: [UnnamedPlanAction] extends [never] ? true : ['unnamed action:', UnnamedPlanAction] =
	true;
void _planActionsAreComplete;

/**
 * The text fields a household may correct through `edit`.
 *
 * Mirrors the backend's `_EDITABLE_FIELDS`. Dates and cadence are `reschedule`'s business, not an
 * edit's, and structural fields are not editable at all — so this is a closed pair, and the endpoint
 * answers anything else with a 422.
 */
export const PlanEditableFields = {
	Title: 'title',
	Description: 'description',
} as const;
export type PlanEditableField = (typeof PlanEditableFields)[keyof typeof PlanEditableFields];

/**
 * The inverse of a forward verb, for a control that has to name the undo it offers.
 *
 * `allowed_actions` already carries whichever half applies, so nothing needs this to decide what to
 * draw. It exists for the reverse lookup — given `uncomplete`, which button was pressed to get here.
 */
export const INVERSE_OF: Partial<Record<PlanAction, PlanAction>> = {
	[PlanActions.Uncomplete]: PlanActions.Complete,
	[PlanActions.Unaccept]: PlanActions.Accept,
	[PlanActions.Unkeep]: PlanActions.Keep,
	[PlanActions.ClearReschedule]: PlanActions.Reschedule,
};
